import {
  formatArrayValue as formatArray,
  formatBooleanValue as formatBoolean,
  formatCurrencyValue as formatCurrency,
  formatDateValue as formatDate,
  formatPercentValue as formatPercent,
} from 'utils/format';

const field = (label, getValue, format) => ({
  label,
  getValue,
  format,
});

export const SECTION_CONFIGS = [
  {
    title: 'Valuation',
    fields: [
      field('Estimated Value', d => d.estimatedValue, formatCurrency),
      field('Estimated Equity', d => d.estimatedEquity, formatCurrency),
      field('Equity Percent', d => d.equityPercent, formatPercent),
      field('Estimated Mortgage Balance', d => d.estimatedMortgageBalance, formatCurrency),
      field('Estimated Mortgage Payment', d => d.estimatedMortgagePayment, formatCurrency),
      field('Open Mortgage Balance', d => d.openMortgageBalance, formatCurrency),
    ],
  },
  {
    title: 'Property Details',
    fields: [
      field('Property Type', d => d.propertyType),
      field('Property Use', d => d.propertyUse),
      field('Property Class', d => d.propertyClass),
      field('Land Use', d => d.landUse),
      field('Year Built', d => d.yearBuilt),
      field('Bedrooms', d => d.bedrooms),
      field('Bathrooms', d => d.bathrooms),
      field('Square Feet', d => d.squareFeet),
      field('Building Sq Ft', d => d.buildingSquareFeet),
      field('Stories', d => d.stories),
      field('Units', d => d.units),
      field('Construction', d => d.construction),
      field('Roof Type', d => d.roofType),
      field('Heating', d => d.heating),
      field('Cooling', d => d.cooling),
      field('Pool', d => d.pool, formatBoolean),
      field('Garage', d => d.garage, formatBoolean),
      field('Fireplace', d => d.fireplaceYn, formatBoolean),
      field('Fireplaces Total', d => d.fireplacesTotal),
      field('Flooring', d => d.flooring),
      field('Appliances', d => d.appliances),
      field('Exterior Features', d => d.exteriorFeatures),
      field('Sewer', d => d.sewer),
      field('Water Source', d => d.waterSource),
    ],
  },
  {
    title: 'Tax & Assessment',
    fields: [
      field('Tax Amount', d => d.taxAmount, formatCurrency),
      field('Tax Year', d => d.taxYear),
      field('Assessed Value', d => d.assessedValue, formatCurrency),
      field('Assessed Land Value', d => d.assessedLandValue, formatCurrency),
      field('Assessed Improvement Value', d => d.assessedImprovementValue, formatCurrency),
    ],
  },
  {
    title: 'Lot Info',
    fields: [
      field('Lot Size (acres)', d => d.acreage),
      field('Lot Size (decimal)', d => d.lotSize),
      field('Lot Sq Ft', d => d.lotSquareFeet),
      field('Lot Depth (ft)', d => d.lotDepthFeet),
      field('Lot Width (ft)', d => d.lotWidthFeet),
      field('Lot Features', d => d.lotFeatures),
      field('APN', d => d.apn),
      field('APN Unformatted', d => d.apnUnformatted),
      field('Lot', d => d.lot),
      field('Block', d => d.block),
      field('Subdivision', d => d.subdivision),
      field('Zoning', d => d.zoning),
      field('Legal Description', d => d.legalDescription),
      field('Legal Section', d => d.legalSection),
      field('Flood Zone', d => d.floodZone, formatBoolean),
      field('Flood Zone Type', d => d.floodZoneType),
      field('Flood Zone Description', d => d.floodZoneDescription),
    ],
  },
  {
    title: 'Ownership',
    fields: [
      field('Owner Names', d => d.ownerNames, formatArray),
      field('Owner Occupied', d => d.ownerOccupied, formatBoolean),
      field('Corporate Owned', d => d.corporateOwned, formatBoolean),
      field('Absentee Owner', d => d.absenteeOwner, formatBoolean),
      field('In-State Absentee Owner', d => d.inStateAbsenteeOwner, formatBoolean),
      field('Out-of-State Absentee Owner', d => d.outOfStateAbsenteeOwner, formatBoolean),
    ],
  },
  {
    title: 'Location',
    fields: [
      field('County', d => d.county),
      field('FIPS', d => d.fips),
      field('Census Tract', d => d.censusTract),
      field('Census Block', d => d.censusBlock),
      field('Census Block Group', d => d.censusBlockGroup),
    ],
  },
  {
    title: 'Property Address',
    fields: [
      field('House', d => d.propertyAddress?.house),
      field('Street', d => d.propertyAddress?.street),
      field('Unit', d => d.propertyAddress?.unit),
      field('City', d => d.propertyAddress?.city),
      field('State', d => d.propertyAddress?.state),
      field('Zip', d => d.propertyAddress?.zip),
      field('County', d => d.propertyAddress?.county),
      field('Latitude', d => d.propertyAddress?.latitude),
      field('Longitude', d => d.propertyAddress?.longitude),
    ],
  },
  {
    title: 'Mailing Address',
    fields: [
      field('House', d => d.mailingAddress?.house),
      field('Street', d => d.mailingAddress?.street),
      field('Unit', d => d.mailingAddress?.unit),
      field('City', d => d.mailingAddress?.city),
      field('State', d => d.mailingAddress?.state),
      field('Zip', d => d.mailingAddress?.zip),
    ],
  },
  {
    title: 'Flags',
    fields: [
      field('Vacant', d => d.vacant, formatBoolean),
      field('Pre-Foreclosure', d => d.preForeclosure, formatBoolean),
      field('Free & Clear', d => d.freeClear, formatBoolean),
      field('Judgment', d => d.judgment, formatBoolean),
      field('Lien', d => d.lien, formatBoolean),
      field('Auction', d => d.auction, formatBoolean),
      field('Cash Buyer', d => d.cashBuyer, formatBoolean),
      field('Cash Sale', d => d.cashSale, formatBoolean),
      field('Death', d => d.death, formatBoolean),
      field('Death Transfer', d => d.deathTransfer, formatBoolean),
      field('Deed In Lieu', d => d.deedInLieu, formatBoolean),
      field('Inherited', d => d.inherited, formatBoolean),
      field('Investor Buyer', d => d.investorBuyer, formatBoolean),
      field('Mobile Home', d => d.mobileHome, formatBoolean),
      field('Private Lender', d => d.privateLender, formatBoolean),
      field('Quit Claim', d => d.quitClaim, formatBoolean),
      field('Sheriffs Deed', d => d.sheriffsDeed, formatBoolean),
      field('Trustee Sale', d => d.trusteeSale, formatBoolean),
      field('Warranty Deed', d => d.warrantyDeed, formatBoolean),
      field('High Equity', d => d.highEquity, formatBoolean),
      field('MFH 2-4', d => d.mfH2to4, formatBoolean),
      field('MFH 5+', d => d.mfH5plus, formatBoolean),
      field('Spousal Death', d => d.spousalDeath, formatBoolean),
    ],
  },
  {
    title: 'MLS Status',
    fields: [
      field('MLS Active', d => d.mlsActive, formatBoolean),
      field('MLS Cancelled', d => d.mlsCancelled, formatBoolean),
      field('MLS Failed', d => d.mlsFailed, formatBoolean),
      field('MLS Has Photos', d => d.mlsHasPhotos, formatBoolean),
      field('MLS Pending', d => d.mlsPending, formatBoolean),
      field('MLS Sold', d => d.mlsSold, formatBoolean),
    ],
  },
  {
    title: 'Demographics',
    fields: [
      field('Median Income', d => d.medianIncome, formatCurrency),
      field('Median Income (detail)', d => d.demographics?.medianIncome, formatCurrency),
      field('FMR Efficiency', d => d.demographics?.fmrEfficiency, formatCurrency),
      field('FMR 1-Bedroom', d => d.demographics?.fmrOneBedroom, formatCurrency),
      field('FMR 2-Bedroom', d => d.demographics?.fmrTwoBedroom, formatCurrency),
      field('FMR 3-Bedroom', d => d.demographics?.fmrThreeBedroom, formatCurrency),
      field('FMR 4-Bedroom', d => d.demographics?.fmrFourBedroom, formatCurrency),
      field('FMR Year', d => d.demographics?.fmrYear),
      field('HUD Area Code', d => d.demographics?.hudAreaCode),
      field('HUD Area Name', d => d.demographics?.hudAreaName),
      field('Suggested Rent', d => d.demographics?.suggestedRent, formatCurrency),
    ],
  },
  {
    title: 'Linked Properties',
    fields: [
      field('Total Owned', d => d.linkedProperties?.totalOwned),
      field('Total Value', d => d.linkedProperties?.totalValue, formatCurrency),
      field('Total Equity', d => d.linkedProperties?.totalEquity, formatCurrency),
      field(
        'Total Mortgage Balance',
        d => d.linkedProperties?.totalMortgageBalance,
        formatCurrency,
      ),
      field('Property IDs', d => d.linkedProperties?.propertyIds, formatArray),
    ],
  },
];

export const MORTGAGE_FIELDS = [
  field('Lender', d => d.lender),
  field('Amount', d => d.amount, formatCurrency),
  field('Rate', d => d.rate, formatPercent),
  field('Type', d => d.type),
  field('Loan Type', d => d.loanType),
  field('Interest Rate Type', d => d.interestRateType),
  field('Term', d => d.term),
  field('Date', d => d.date, formatDate),
  field('Due Date', d => d.dueDate, formatDate),
  field('Recording Date', d => d.recordingDate, formatDate),
  field('Maturity Date', d => d.maturityDate, formatDate),
  field('Position', d => d.position),
  field('Deed Type', d => d.deedType),
  field('Lender Type', d => d.lenderType),
  field('Grantee Name', d => d.granteeName),
  field('Open', d => d.open, formatBoolean),
  field('Assumable', d => d.assumable, formatBoolean),
  field('Transaction Type', d => d.transactionType),
];

export const SALE_FIELDS = [
  field('Date', d => d.date, formatDate),
  field('Price', d => d.price, formatCurrency),
  field('Buyer', d => d.buyer),
  field('Seller', d => d.seller),
  field('Document Type', d => d.documentType),
  field('Arms Length', d => d.armsLength, formatBoolean),
  field('Down Payment', d => d.downPayment, formatCurrency),
  field('LTV', d => d.ltv, formatPercent),
  field('Purchase Method', d => d.purchaseMethod),
  field('Transaction Type', d => d.transactionType),
  field('Recording Date', d => d.recordingDate, formatDate),
  field('Prior Owner Months Owned', d => d.priorOwnerMonthsOwned),
];

export const FORECLOSURE_FIELDS = [
  field('Original Loan Amount', d => d.originalLoanAmount, formatCurrency),
  field('Default Amount', d => d.defaultAmount, formatCurrency),
  field('Estimated Bank Value', d => d.estimatedBankValue, formatCurrency),
  field('Opening Bid', d => d.openingBid, formatCurrency),
  field('Recording Date', d => d.recordingDate, formatDate),
  field('Auction Date', d => d.auctionDate, formatDate),
  field('Auction Time', d => d.auctionTime),
  field('Auction Address', d => d.auctionStreetAddress),
  field('Document Type', d => d.documentType),
  field('Trustee Sale Number', d => d.trusteeSaleNumber),
  field('Type', d => d.typeName),
  field('Active', d => d.active, formatBoolean),
];

export const MLS_HISTORY_FIELDS = [
  field('Status', d => d.status),
  field('Status Date', d => d.statusDate, formatDate),
  field('Price', d => d.price, formatCurrency),
  field('Type', d => d.type),
  field('Beds', d => d.beds),
  field('Baths', d => d.baths),
  field('Days on Market', d => d.daysOnMarket),
  field('Agent Name', d => d.agentName),
  field('Agent Office', d => d.agentOffice),
  field('Agent Phone', d => d.agentPhone),
];

export const AUCTION_DETAIL_FIELDS = [
  field('Active', d => d.active, formatBoolean),
  field('Auction Date', d => d.auctionDate, formatDate),
  field('Case Number', d => d.caseNumber),
  field('Judgment Amount', d => d.judgmentAmount, formatCurrency),
  field('Judgment Date', d => d.judgmentDate, formatDate),
  field('Lender Name', d => d.lenderName),
  field('Opening Bid', d => d.openingBid, formatCurrency),
  field('Original Loan Amount', d => d.originalLoanAmount, formatCurrency),
  field('Document Type', d => d.documentType),
  field('Trustee Name', d => d.trusteeFullName),
  field('Trustee Phone', d => d.trusteePhone),
  field('Trustee Sale Number', d => d.trusteeSaleNumber),
  field('Notice Type', d => d.noticeType),
  field('Default Amount', d => d.defaultAmount, formatCurrency),
  field('Recording Date', d => d.recordingDate, formatDate),
];
